import React, { useState, useEffect } from 'react';
import PromptCard from './PromptCard';
import { PromptCard as PromptCardType, PromptGroup as PromptGroupType } from '../types';
import { useConfirmDialog } from '../providers/DialogProvider';

interface PromptGroupProps {
  group: PromptGroupType;
  cards: PromptCardType[];
  onCardClick: (query: string) => void;
  onEditCard: (card: PromptCardType) => void;
  onDeleteCard: (cardId: string) => void;
  onMoveCard: (card: PromptCardType) => void;
  workgroupId?: string | null;
  isEditable?: boolean;
  onEditGroup?: (group: PromptGroupType) => void;
  onDeleteGroup?: (groupId: string) => void;
}

const PromptGroup: React.FC<PromptGroupProps> = ({
  group,
  cards,
  onCardClick,
  onEditCard,
  onDeleteCard,
  onMoveCard,
  workgroupId = null,
  isEditable = false,
  onEditGroup,
  onDeleteGroup
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [groupCards, setGroupCards] = useState<PromptCardType[]>([]);
  const { confirm } = useConfirmDialog();

  useEffect(() => {
    // Filter cards to only show the ones in this group
    if (group && group.cards && cards) {
      const filteredCards = cards.filter(card => group.cards.includes(card.id));
      setGroupCards(filteredCards);
    }
  }, [group, cards]);

  const handleEditGroup = () => {
    onEditGroup && onEditGroup(group);
  };

  const handleDeleteGroup = async () => {
    console.log('🔄 Starting delete group flow from PromptGroup component for group:', group.name);
    try {
      const confirmed = await confirm(`Are you sure you want to delete the "${group.name}" group?`, 'delete_group');
      console.log('✅ Custom confirm dialog response:', confirmed);
      
      if (confirmed) {
        console.log('🗑️ User confirmed deletion, calling onDeleteGroup...');
        onDeleteGroup && onDeleteGroup(group.id);
      } else {
        console.log('❌ User cancelled deletion');
      }
    } catch (error) {
      console.error('❌ Error in PromptGroup handleDeleteGroup:', error);
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-600 dark:text-gray-400 mr-2"
            aria-label={isExpanded ? "Collapse group" : "Expand group"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              {isExpanded ? (
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              ) : (
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              )}
            </svg>
          </button>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{group.name}</h3>
        </div>
        
        {isEditable && (
          <div className="flex space-x-2">
            <button 
              onClick={handleEditGroup}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Edit group"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
            {!group.isDefault && (
              <button 
                onClick={handleDeleteGroup}
                className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                aria-label="Delete group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
      
      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
          {groupCards.length > 0 ? (
            groupCards.map((card) => (
              <div key={card.id} className="relative">
                <PromptCard
                  title={card.title}
                  description={card.description || ''}
                  onClick={() => onCardClick(card.query)}
                  onEdit={() => onEditCard(card)}
                  onDelete={() => onDeleteCard(card.id)}
                  isEditable={isEditable}
                />
                {isEditable && (
                  <button
                    onClick={() => onMoveCard(card)}
                    className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full p-1 shadow-md hover:bg-blue-600 transition-colors"
                    title="Move to different group"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                  </button>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 col-span-2">No prompt cards in this group yet.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptGroup;
